import { WindowsAdvancedElementImportErrorPopup } from '@modules/test-development/components/elements/components/windows-advanced-element-import-popups/failed';
import { WindowsAdvancedElementImportProcessingPopup } from '@modules/test-development/components/elements/components/windows-advanced-element-import-popups/processing';
import { WindowsAdvancedElementImportSummaryPopup } from '@modules/test-development/components/elements/components/windows-advanced-element-import-popups/summary';
import { WindowsAdvancedElementImportPopup } from '@modules/test-development/components/elements/components/windows-advanced-element-import-popups/upload';
import { WindowsAdvancedElementsContext } from '@modules/test-development/components/elements/list/windows-advanced-elements-context';
import ElementService from '@services/element.service';
import { useCallback, useContext, useEffect, useState } from 'react';
import { toastify, useInternalEvents, useTranslation } from '@testsigma/atoms';
import { UserPreferenceContext } from '@contexts/user-preference-context';

import dayjs from 'dayjs';

const formatTimestamp = (timestamp) => {
  const momentDate = dayjs(timestamp);
  const formattedDate = momentDate.format('hh:mm A, DD MMM, YYYY');

  return formattedDate;
};

const getFileData = (res: any) => {
  const misc = JSON.parse(res.misc);
  const data = {
    file: res.name,
    updatedOn: formatTimestamp(res.createdAt),
    added: misc.newElementsCount,
    updated: misc.modifiedElementsCount,
    deleted: misc.deletedElementsCount,
    total: misc.totalElements,
  };

  return data;
};

export const WindowsAdvancedProvider = ({ children }) => {
  const [currentPopup, updateCurrentPopup] = useState(null);
  const [isWindowsAdvanced,  updateIsWindowsAdvanced] = useState(false);
  const [isImporting, updateIsImporting] = useState(false);
  const [isUploading, updateIsUploading] = useState(false);
  const [uftCurrentImportItem, updateUFTCurrentImportItem] = useState(null);
  const [currentElementFile, _updateCurrentElementFile] = useState(null);
  const { userPreference } = useContext(UserPreferenceContext);

  const updateCurrentElementFile = (value) => {
    _updateCurrentElementFile(value);
  };

  const publish = useInternalEvents([]);

  const { t } = useTranslation();

  const handleUpload = (dataFile, versionId) => {
    const params = {
      versionId,
      isReplace: true,
    };

    updateIsUploading(true);

    ElementService.importWindowsAdvancedData(dataFile, params)
      .then((res) => {
        updateIsUploading(false);
        localStorage.setItem('uftCurrentImportItem', JSON.stringify(res));
        updateUFTCurrentImportItem(res);
        updateCurrentPopup({ popup: 'processing' });
        updateIsImporting(true);
      })
      .catch(() => {
        updateIsUploading(false);
        updateCurrentPopup({ popup: null });
        toastify(t('element.list.import_failed'), { type: 'error' });
      });
  };

  const getLastImportStatus = (appVersionId) => {
    ElementService.getLastWindowsAdvancedImportStatus(appVersionId).then((res: any) => {
      if (res?.misc && res?.applicationVersion?.id == appVersionId) {
        updateCurrentElementFile(getFileData(res));
      } else {
        updateCurrentElementFile(null);
      }
    });
  };

  const handleStatusSync = () => {
    let item: any = localStorage.getItem('uftCurrentImportItem');
    if (item) {
      item = JSON.parse(item);
      ElementService.getWindowsAdvancedImportStatus(item?.id).then((res: any) => {
        if (res.status == 'IN_PROGRESS') {
          localStorage.setItem('uftCurrentImportItem', JSON.stringify(res));
          window.setTimeout(() => updateUFTCurrentImportItem(res), 3000);
          updateIsImporting(true);
        } else {
          localStorage.removeItem('uftCurrentImportItem');
          updateIsImporting(false);
          updateUFTCurrentImportItem(null);
          updateCurrentPopup(null);
          if (res.status == 'SUCCESS') {
            toastify(
              () => (
                <>
                  {t('uft.toast.success')}{' '}
                  <span
                    className='text-blue-600'
                    onClick={() => updateCurrentPopup({ popup: 'summary' })}
                  >
                    {t('common.view_summary')}
                  </span>
                </>
              ),
              {
                type: 'success',
                position: 'top-center',
                hideProgressBar: true,
              },
            );

            updateCurrentElementFile(getFileData(res));

            // if user is on elements listing page then refresh on success
            if (window.location.href.match(/\/td\/[0-9]+\/elements\//))
              publish('refresh-list');
          } else if (res.status == 'FAILURE') {
            toastify(
              () => (
                <>
                  {t('uft.toast.failed')}{' '}
                  <span
                    className='text-blue-600'
                    onClick={() => updateCurrentPopup({ popup: 'failed' })}
                  >
                    {t('common.know_more')}
                  </span>
                </>
              ),
              {
                type: 'error',
                position: 'top-center',
                hideProgressBar: true,
              },
            );
          }
        }
      });
    }
  };

  useEffect(() => {
    // @ts-ignore
    window.uft = {
      currentPopup,
      updateCurrentPopup,
      isWindowsAdvanced,
      updateIsWindowsAdvanced,
      isImporting,
      updateIsImporting,
      currentElementFile,
      updateCurrentElementFile,
      isUploading,
      updateIsUploading,
    };
  }, [
    currentPopup,
    updateCurrentPopup,
    isWindowsAdvanced,
    updateIsWindowsAdvanced,
    isImporting,
    updateIsImporting,
    currentElementFile,
    updateCurrentElementFile,
    isUploading,
    updateIsUploading,
  ]);

  useEffect(() => {
    handleStatusSync();
  }, [uftCurrentImportItem]);

  useEffect(() => {
    if (userPreference.preference?.projectPreference?.versionId) {
      getLastImportStatus(
        userPreference.preference?.projectPreference?.versionId,
      );
    }
  }, [userPreference.preference?.projectPreference?.versionId]);

  return (
    <WindowsAdvancedElementsContext.Provider
      value={{
        currentPopup,
        updateCurrentPopup,
        isWindowsAdvanced,
        updateIsWindowsAdvanced,
        isImporting,
        updateIsImporting,
        currentElementFile,
        updateCurrentElementFile,
        isUploading,
        updateIsUploading,
        handleUpload,
        uftCurrentImportItem,
        updateUFTCurrentImportItem,
      }}
    >
      {children}
      <UFTPopup />
    </WindowsAdvancedElementsContext.Provider>
  );
};

const UFTPopup = () => {
  const { currentPopup } = useContext(WindowsAdvancedElementsContext);

      if (currentPopup?.popup == 'import') return <WindowsAdvancedElementImportPopup />;
    else if (currentPopup?.popup == 'processing')
      return <WindowsAdvancedElementImportProcessingPopup />;
    else if (currentPopup?.popup == 'failed')
      return <WindowsAdvancedElementImportErrorPopup />;
    else if (currentPopup?.popup == 'summary')
      return <WindowsAdvancedElementImportSummaryPopup />;

  return null;
};
